import React from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import Reveal from '../components/Reveal';
import MarketingDemo from '../components/MarketingDemo';
import CtaBlock from '../components/CtaBlock';
import AdLibrary from '../components/AdLibrary';
import {
  CITIES,
  MARKETING_HEADLINE,
  MARKETING_MONTH,
  MARKETING_SUBHEAD,
  PRICING_NOTE,
} from '../data/site';
import { track, EVENTS } from '../utils/analytics';

const META_TITLE = 'Ads & marketing | JK Automations';
const META_DESCRIPTION = `${MARKETING_SUBHEAD} ${CITIES}.`;

/**
 * The money page. Same story as the homepage, told in full for the one
 * department that is live - a month of work, the creative, and the receipts.
 */
const Marketing: React.FC = () => {
  return (
    <>
      <PageMeta title={META_TITLE} description={META_DESCRIPTION} path="/marketing" />

      <section className="hero hero--page">
        <div className="shell hero__inner">
          <div className="hero__copy">
            <p className="meet__kicker">Ads &amp; marketing</p>
            <h1 className="display">{MARKETING_HEADLINE}</h1>
            <p className="hero__sub">{MARKETING_SUBHEAD}</p>
            <div className="hero__actions">
              <Link
                to="/contact"
                className="button button--primary"
                onClick={() =>
                  track(EVENTS.ctaClicked, { location: 'marketing-hero', label: 'Book a call' })
                }
              >
                Book a call
              </Link>
            </div>
            <p className="hero__meta">
              <span>Live today</span>
              <span>{CITIES}</span>
            </p>
          </div>
          <div className="hero__stage">
            <MarketingDemo />
          </div>
        </div>
      </section>

      {/* ---- A month ---- */}
      <section id="month" className="band band--tint">
        <div className="shell">
          <Reveal className="lede-row">
            <h2 className="h2">What full-service marketing can look like.</h2>
            <p>If your team only needs creative, campaign help or reporting, we scope that part instead. Nothing sends or spends without your approval.</p>
          </Reveal>

          <Reveal className="month" index={1}>
            {MARKETING_MONTH.map((w) => (
              <div className="week" key={w.week}>
                <span className="week__n">{w.week}</span>
                <h3 className="week__title">{w.title}</h3>
                <p className="week__body">{w.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ---- The creative ---- */}
      <section id="creative" className="band">
        <div className="shell">
          <Reveal className="lede-row">
            <h2 className="h2">The library.</h2>
            <p>
              A selection of what we make: software ads for our own products, generated commercials,
              and scene studies from our in-house video pipeline. Campaign results come from the
              accounts and reports, not from the production method.
            </p>
          </Reveal>
          <Reveal index={1}>
            <AdLibrary />
          </Reveal>
        </div>
      </section>

      {/* ---- Receipts ---- */}
      <section id="receipts" className="band band--arceus">
        <div className="shell receipts">
          <Reveal className="receipts__copy">
            <h2 className="h2">Every number has a source.</h2>
            <p>
              Agencies send a deck. We send the run: what we read, what we did, what it cost, and
              what booked, each line tied to the account it came from.
            </p>
            <ul className="receipts__list">
              <li>Nothing sends or spends until you approve it, and each approved step is logged.</li>
              <li>Every figure carries the account it was read from and when.</li>
              <li>You own the ad accounts. Always.</li>
            </ul>
          </Reveal>
          <Reveal className="receipts__aside" index={1}>
            <div className="pricing pricing--stack">
              <div className="pricing__copy">
                {PRICING_NOTE.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
              <Link
                to="/contact"
                className="button button--primary"
                onClick={() =>
                  track(EVENTS.ctaClicked, { location: 'marketing-receipts', label: 'Book a call' })
                }
              >
                Book a call
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBlock
        where="marketing"
        title="Tell us what your marketing costs you now."
        lede="We will tell you what we would fix first, and what it costs."
      />
    </>
  );
};

export default React.memo(Marketing);
